import React, { useCallback, useMemo } from 'react';
import { Modal } from '@mantine/core';
import { useMutation } from '@apollo/client';
import AcceptPriceChangeRequestForm from './AcceptPriceChangeRequestForm';
import DeclinePriceChangeRequestForm from './DeclinePriceChangeRequestForm';
import SuggestInterimPriceForm from './SuggestInterimPriceForm';
import { ACTION_PRICE_CHANGE_REQUEST } from '../../apollo/mutations/priceChangeRequest';
import { PriceChangeRequestAction } from '../../codegen/graphql';
import {
    showErrorNotification,
    showLoadingNotification,
    updateFailedNotification,
    updateSuccessNotification,
} from '../../notifications';
import { getErrorMessage } from '../../helpers/getErrorMessage';
import { PriceChangeRequestMetadata } from '../../types/price-change-request';
import {
    GET_AD_ZONE_STORES_FOR_REQUEST,
    GET_CATEGORY_STATS,
    GET_PRICE_CHANGE_REQUEST_HISTORY,
    GET_PRICE_CHANGE_REQUEST_TABLE_ROW,
    GET_PRICE_CHANGE_REQUESTS_TABLE_DATA,
    GET_REQUEST_ESCALATION_MESSAGE,
    GET_SAP_OUTCOMES_FOR_REQUEST,
    GET_STATUS_COUNTS,
} from '../../apollo/queries/priceChangeRequest';
import AcceptPriceChangeRequestFormOps from './AcceptPriceChangeRequestFormOps';
import AcceptPriceChangeRequestFormDm from './AcceptPriceChangeRequestFormDm';
import DeclinePriceChangeRequestFormOps from './DeclinePriceChangeRequestFormOps';
import {
    GET_ACTION_CARD_ITEMS,
    GET_ACTIONS_FOR_REQUEST,
} from '../../apollo/queries/priceChangeRequestActions';
import SupplierFeedbackForm from './SupplierFeedbackForm';
import { useAuthStore } from '../../stores/auth.store';
import { useRequestDrawer } from '../../context/RequestDrawerContext';
import { When } from '../common';
import { trackPriceChangeTimeToAction } from '../../helpers/priceChangeRequestTracking';

type Props = {
    opened: boolean;
    verb: string | null;
    close: () => void;
    closeDrawer: () => void;
};

function PriceChangeRequestModal({ opened, verb, close, closeDrawer }: Props) {
    const {
        flatRequest,
        actions: contextActions,
        adZoneStores,
        currentPrices,
        drawerOpenedAt,
    } = useRequestDrawer();
    const { userId, standInForUserIds } = useAuthStore();

    const allResponsibleUserIds = useMemo(
        () => [userId, ...standInForUserIds],
        [userId, standInForUserIds],
    );

    const actions: PriceChangeRequestAction[] = contextActions;

    let requestAction = actions.find(
        (action: PriceChangeRequestAction) =>
            allResponsibleUserIds.includes(action.responsibleUser?.id) &&
            !action?.completed,
    );

    if (!requestAction) {
        [requestAction] = actions;
    }

    const [actionPriceChangeRequest] = useMutation(
        ACTION_PRICE_CHANGE_REQUEST,
        {
            refetchQueries: [
                GET_ACTION_CARD_ITEMS,
                GET_ACTIONS_FOR_REQUEST,
                GET_STATUS_COUNTS,
                GET_CATEGORY_STATS,
                GET_PRICE_CHANGE_REQUESTS_TABLE_DATA,
                GET_PRICE_CHANGE_REQUEST_TABLE_ROW,
                GET_PRICE_CHANGE_REQUEST_HISTORY,
                GET_REQUEST_ESCALATION_MESSAGE,
                GET_AD_ZONE_STORES_FOR_REQUEST,
                GET_SAP_OUTCOMES_FOR_REQUEST,
            ],
            awaitRefetchQueries: false,
        },
    );

    const title = useMemo(() => {
        switch (verb) {
            case 'accept':
                return 'Accept Price Change Request';
            case 'decline':
                return 'Reject Price Change Request';
            case 'accept-supplier':
                return 'Supplier Approved';
            case 'decline-supplier':
                return 'Supplier Declined';
            default:
                return 'Suggest Interim Price';
        }
    }, [verb]);

    const onConfirm = useCallback(
        async (input: {
            outcome: string;
            notes: string;
            metadata: PriceChangeRequestMetadata;
        }) => {
            const canSubmitRequest =
                verb === 'accept' || verb === 'suggest'
                    ? requestAction?.possibleOutcomes?.includes('Approved')
                    : requestAction?.possibleOutcomes?.includes('Declined');

            if (!canSubmitRequest) {
                showErrorNotification('Unable to submit request');
                return;
            }
            if (!requestAction?.id) {
                showErrorNotification('Something went wrong');
                return;
            }
            try {
                const timeToActionSeconds =
                    drawerOpenedAt != null
                        ? Math.round((Date.now() - drawerOpenedAt) / 1000)
                        : undefined;

                if (timeToActionSeconds != null && flatRequest?.id) {
                    trackPriceChangeTimeToAction({
                        timeToActionSeconds,
                        outcome: input.outcome,
                        actionType: requestAction?.action ?? 'unknown',
                    });
                }

                let message = `${
                    verb === 'accept' || verb === 'accept-supplier'
                        ? 'Accepting'
                        : 'Declining'
                } price change request.`;
                if (verb === 'suggest') {
                    message =
                        'Accepting price change request with interim price.';
                }
                const loadingTitle =
                    verb === 'accept' ||
                    verb === 'suggest' ||
                    verb === 'accept-supplier'
                        ? 'Accepting'
                        : 'Declining';
                showLoadingNotification(
                    'apply-price-change-request',
                    loadingTitle,
                    message,
                );
                await actionPriceChangeRequest({
                    variables: {
                        input: {
                            outcome: input.outcome,
                            declinedReason: input.metadata.reason || null,
                            note: input.notes,
                            metadata: input.metadata,
                            actionId: requestAction.id,
                        },
                    },
                });
                message = `Price change request ${
                    verb === 'accept' || verb === 'accept-supplier'
                        ? 'accepted'
                        : 'declined'
                }.`;
                if (verb === 'suggest') {
                    message =
                        'Price change request accepted with interim price';
                }
                updateSuccessNotification(
                    'apply-price-change-request',
                    verb === 'accept' ||
                        verb === 'suggest' ||
                        verb === 'accept-supplier'
                        ? 'Accepted'
                        : 'Declined',
                    message,
                );
                closeDrawer();
            } catch (error) {
                updateFailedNotification(
                    'apply-price-change-request',
                    'Failed',
                    getErrorMessage(error),
                );
            }
        },
        [verb, requestAction, drawerOpenedAt, flatRequest, closeDrawer],
    );

    const request = flatRequest;

    const matchingStore = useMemo(() => {
        if (!request) {
            return null;
        }
        const fromZone = adZoneStores.find(zoneStore => {
            const matchesStoreNo = zoneStore.storeNo === request.storeNo;
            const matchesStoreId = zoneStore.id === request.storeId;
            return matchesStoreNo || matchesStoreId;
        });
        if (fromZone) {
            return fromZone;
        }
        return request.store ?? null;
    }, [request, adZoneStores]);

    const renderContent = useCallback(() => {
        if (!request) return null;

        if (requestAction?.action === 'DivisionManagerApproval') {
            return (
                <When
                    condition={verb === 'accept'}
                    fallback={
                        <DeclinePriceChangeRequestFormOps
                            request={request}
                            onConfirm={onConfirm}
                            close={close}
                        />
                    }
                >
                    <AcceptPriceChangeRequestFormDm
                        request={request}
                        store={matchingStore}
                        advertisingZoneStores={adZoneStores}
                        onConfirm={onConfirm}
                        close={close}
                        showPriceChangeFreezeBanner
                    />
                </When>
            );
        }

        if (
            requestAction?.action === 'OpsExecutiveEscalationApproval' ||
            requestAction?.action === 'OpsExecutivePriceChangeFreezeApproval' ||
            requestAction?.action === 'SeniorOpsExecutiveEscalationApproval' ||
            requestAction?.action === 'SeniorOpsFreezeSecondaryEscalation'
        ) {
            return (
                <When
                    condition={verb === 'accept'}
                    fallback={
                        <DeclinePriceChangeRequestFormOps
                            request={request}
                            onConfirm={onConfirm}
                            close={close}
                        />
                    }
                >
                    <AcceptPriceChangeRequestFormOps
                        request={request}
                        store={matchingStore}
                        advertisingZoneStores={adZoneStores}
                        onConfirm={onConfirm}
                        close={close}
                    />
                </When>
            );
        }

        switch (verb) {
            case 'accept':
                return (
                    <AcceptPriceChangeRequestForm
                        request={request}
                        store={matchingStore}
                        advertisingZoneStores={adZoneStores}
                        onConfirm={onConfirm}
                        close={close}
                        currentPrices={currentPrices}
                    />
                );
            case 'decline':
                return (
                    <DeclinePriceChangeRequestForm
                        request={request}
                        onConfirm={onConfirm}
                        close={close}
                    />
                );
            case 'accept-supplier':
                return (
                    <SupplierFeedbackForm
                        isDecline={false}
                        request={request}
                        store={matchingStore}
                        advertisingZoneStores={adZoneStores}
                        onConfirm={onConfirm}
                        close={close}
                    />
                );
            case 'decline-supplier':
                return (
                    <SupplierFeedbackForm
                        isDecline
                        request={request}
                        store={matchingStore}
                        advertisingZoneStores={adZoneStores}
                        onConfirm={onConfirm}
                        close={close}
                    />
                );
            default:
                return (
                    <SuggestInterimPriceForm
                        request={request}
                        store={matchingStore}
                        advertisingZoneStores={adZoneStores}
                        onConfirm={onConfirm}
                        close={close}
                        currentPrices={currentPrices}
                    />
                );
        }
    }, [
        verb,
        request,
        requestAction,
        onConfirm,
        close,
        currentPrices,
        matchingStore,
        adZoneStores,
    ]);

    return (
        <Modal
            opened={opened}
            onClose={close}
            title={title}
            classNames={{
                title: 'text-primary font-semibold text-lg',
                overlay: 'z-[700]',
                inner: 'z-[800]',
            }}
            size="lg"
            keepMounted={false}
        >
            {renderContent()}
        </Modal>
    );
}

export default PriceChangeRequestModal;
